import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users, Building2, Mail, Phone, MapPin, Upload, FileText } from 'lucide-react';

const CustomersPage = () => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    billing_name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    cust_id: '',
  });

  useEffect(() => {
    fetchCustomers();
  }, [searchQuery]);

  const fetchCustomers = async () => {
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await api.get('/customers', { params });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.billing_name.trim()) {
      toast.error('Billing Name is required');
      return;
    }
    if (!formData.contact_person.trim()) {
      toast.error('Contact Person is required');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone Number is required');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success('Customer updated successfully');
      } else {
        await api.post('/customers', formData);
        toast.success('Customer created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('Failed to save customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      billing_name: customer.billing_name || '',
      contact_person: customer.contact_person || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      cust_id: customer.cust_id || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await api.delete(`/customers/${customerId}`);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      billing_name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      cust_id: '',
    });
    setEditingCustomer(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter((line) => line.trim());
        const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());

        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          const row = {};
          headers.forEach((header, idx) => {
            const key = header.replace(/\s+/g, '_');
            row[key] = values[idx]?.trim() || '';
          });
          rows.push(row);
        }

        const response = await api.post('/customers/bulk-import', { rows });
        setBulkImportResult(response.data);
        setShowBulkDialog(true);
        fetchCustomers();
        toast.success(`Imported ${response.data.imported} customers`);
      } catch (error) {
        console.error('Bulk import failed:', error);
        toast.error(error.response?.data?.detail || 'Failed to import customers');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const csvContent = 'billing_name,contact_person,phone,email,address,cust_id\nAcme Corporation,John Doe,9876543210,john@acme.com,123 Main St,CUST001';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'customer_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Customer Management
          </h1>
          <p className="text-muted-foreground">
            Manage your customers and their information
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="file"
              accept=".csv"
              onChange={handleBulkImport}
              className="hidden"
              id="bulk-import-input"
              data-testid="bulk-import-input"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('bulk-import-input').click()}
              data-testid="bulk-import-button"
              className="h-12"
            >
              <Upload className="w-5 h-5 mr-2" />
              Bulk Import
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={downloadTemplate}
            data-testid="download-template-button"
          >
            <FileText className="w-4 h-4 mr-1" />
            Template
          </Button>
          <Button
            onClick={openNewDialog}
            data-testid="add-customer-button"
            size="lg"
            className="h-12 px-6 font-bold"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search customers by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="customer-search-input"
            className="pl-10 h-12"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Customers</p>
              <p className="text-2xl font-bold">{customers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Phone className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">With Phone</p>
              <p className="text-2xl font-bold">
                {customers.filter((c) => c.phone).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">With Email</p>
              <p className="text-2xl font-bold">
                {customers.filter((c) => c.email).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        {customers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No customers found</p>
            <p className="text-sm text-muted-foreground">Add your first customer to get started</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Billing Name</TableHead>
                <TableHead>Contact Person</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead className="hidden lg:table-cell">Email</TableHead>
                <TableHead className="hidden lg:table-cell">Cust ID</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{customer.billing_name}</p>
                      <p className="text-sm text-muted-foreground md:hidden">
                        {customer.phone || '-'}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {customer.contact_person || '-'}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {customer.phone || '-'}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {customer.email || '-'}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {customer.cust_id || '-'}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {customer.phone || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(customer.id)}
                          data-testid={`delete-customer-${customer.id}`}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer
                ? 'Update customer information'
                : 'Fields marked with * are required'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="billing_name">Billing Name *</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="billing_name"
                    value={formData.billing_name}
                    onChange={(e) =>
                      setFormData({ ...formData, billing_name: e.target.value })
                    }
                    placeholder="Enter billing name / company"
                    data-testid="billing-name-input"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact_person">Contact Person *</Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) =>
                      setFormData({ ...formData, contact_person: e.target.value })
                    }
                    placeholder="Contact person name"
                    data-testid="contact-person-input"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="+91 98765 43210"
                    data-testid="customer-phone-input"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="email@example.com"
                    data-testid="customer-email-input"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    placeholder="Customer address"
                    data-testid="customer-address-input"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cust_id">Cust ID</Label>
                <Input
                  id="cust_id"
                  value={formData.cust_id}
                  onChange={(e) =>
                    setFormData({ ...formData, cust_id: e.target.value })
                  }
                  placeholder="Optional customer ID"
                  data-testid="cust-id-input"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-customer-button" disabled={submitting}>
                {submitting ? 'Saving...' : (editingCustomer ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Result Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Import Results</DialogTitle>
          </DialogHeader>
          {bulkImportResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">{bulkImportResult.imported}</p>
                  <p className="text-sm text-muted-foreground">Imported</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">{bulkImportResult.errors?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </div>
              </div>
              {bulkImportResult.errors?.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Errors:</p>
                  <div className="max-h-40 overflow-y-auto bg-muted rounded-lg p-3">
                    {bulkImportResult.errors.map((error, idx) => (
                      <p key={idx} className="text-sm text-red-600">{error}</p>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={() => setShowBulkDialog(false)} className="w-full">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
